import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal — lightweight scroll-triggered fade-in wrapper.
 * Uses IntersectionObserver. Triggers once per element by default.
 *
 * Props:
 *  - delay: number (ms) — animation delay
 *  - y: number (px) — initial translateY offset (default 28)
 *  - as: tag name (default 'div')
 *  - once: boolean (default true)
 *  - threshold: number (default 0.12)
 *  - className: extra classes
 *  - rootMargin: string (default '0px 0px -8% 0px')
 */
const Reveal = ({
  children,
  delay = 0,
  y = 28,
  as: Tag = 'div',
  once = true,
  threshold = 0.12,
  rootMargin = '0px 0px -8% 0px',
  className = '',
  ...rest
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            if (once) observer.unobserve(entry.target);
          } else if (!once) {
            setVisible(false);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [once, threshold, rootMargin]);

  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'is-visible' : ''} ${className}`}
      style={{
        '--reveal-delay': `${delay}ms`,
        '--reveal-y': `${y}px`,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
};

export default Reveal;
